// type-only: fitness-plan.ts calls back into this module, and a type import is erased
// at compile time, so the two files cannot form a runtime import cycle.
import type { Plan_ExercisePath, Plan_SubSlot } from './fitness-plan';

/**
 * How many sets an exercise in the plan really holds, and how to write that number back.
 *
 * The count lives in two places. `Plan_SubSlot.defaultVolume_sets` is the base number.
 * On top of it sits `volumeAdjustment` on an exercise PATH — the -1, 0 or 1 that volume
 * autoregulation stores when the user accepts "we recommend adjusting your set volume"
 * during a workout. Only `buildSets` (client/src/services/workout-loader.service.ts)
 * ever added the two together, so the workouts were right while every plan-level screen
 * read the base alone and showed a number the user's own workouts contradicted (#4523).
 *
 * See design-docs/volume-autoregulation.md for the full rule and for the three readers
 * that deliberately stay on the base number.
 */

/** The path the next workout for this sub-slot will use. Mirrors `setCurrentPath` in
 *  client/src/services/workout-loader.service.ts: the path for the stored next
 *  intensity, and the first path when there is no such path. This is a plan-level
 *  answer, so it deliberately knows nothing about a workout's `planSubslotBackup`. */
function pathForNextWorkout(subSlot: Plan_SubSlot): Plan_ExercisePath | undefined {
  const paths = subSlot.exercisePaths || [];
  const forNextIntensity = subSlot.nextWorkoutIntensity
    && paths.find(p => p.intensityLevel === subSlot.nextWorkoutIntensity);
  return forNextIntensity || paths[0];
}

/**
 * The number of sets the next workout for this exercise will actually hold.
 *
 * For a periodized exercise each intensity has its own path and so its own adjustment.
 * The answer describes the session that comes NEXT; the session after it can differ by
 * one set. That imprecision is inherent to a plan editor that shows one number per
 * exercise, and it is far smaller than reading the base count alone.
 *
 * @param usesMyoReps whether this exercise is scheduled as myo-reps. Work it out with
 *   `exerciseUsesMyoReps` in ./exercise-myoreps — it needs the Exercise document and the
 *   user's state, which this module deliberately does not take. When true the base count
 *   is returned unchanged, because `buildSets`' myo-rep branch never applies the
 *   adjustment either: a displayed number has to agree with the workout, not improve on it.
 */
export function getEffectivePlanSetCount(subSlot: Plan_SubSlot, usesMyoReps: boolean): number {
  if (usesMyoReps) {
    return subSlot.defaultVolume_sets;
  }

  const path = pathForNextWorkout(subSlot);
  const numSets = subSlot.defaultVolume_sets + (path?.volumeAdjustment || 0);

  // never answer with less than one set. the test is "not at least 1" rather than
  // "less than 1" on purpose, and swapping it back silently reopens the road it
  // closes: neither client nor shared has strictNullChecks, so an absent
  // defaultVolume_sets really can arrive here, the addition above makes NaN of it,
  // and `NaN < 1` is FALSE. This is the same guard, for the same reason, as the one
  // in buildSets (client/src/services/workout-loader.service.ts).
  return numSets >= 1 ? numSets : 1;
}

/**
 * Store a set count the user (or volume optimization) decided on.
 *
 * The number becomes the base, and the adjustment is cleared on EVERY path. Both halves
 * matter. Without the clearing the adjustment is added on top of the number just
 * written, which is the second half of #4523: a user shown 2, who performs 3 and types
 * 3, ended up with workouts of 4. And it has to be every path rather than only the next
 * one, because the typed number covers the whole exercise while an adjustment left on
 * another intensity's path would come back as an extra set on that session.
 *
 * Clearing rather than subtracting is deliberate. The base is one number per sub-slot
 * while the adjustment is per path, so there is no single "base = typed - adjustment"
 * for a periodized exercise. It also gives autoregulation the right anchor:
 * `computeVolumeAutoregulation` measures against the base and keeps the adjustment
 * within one set of it, so a deliberate decision should become the new anchor rather
 * than being treated as already adjusted.
 */
export function setPlanSetCount(subSlot: Plan_SubSlot, newSets: number): void {
  subSlot.defaultVolume_sets = newSets;
  for (const path of subSlot.exercisePaths || []) {
    path.volumeAdjustment = 0;
  }
}

/**
 * Reset to 0 every path adjustment that would leave this exercise with no sets.
 *
 * An adjustment is agreed against one base. A program rebuild, Restore Original Plan and
 * a swap that seeds a fresh sub-slot from history all put the paths onto a DIFFERENT
 * base and keep the adjustment, on purpose. Where the new base is 1, a carried -1 would
 * plan 0 sets, which `buildSets` then has to correct and report (#6665). Only that case
 * is reset; a -1 that still leaves a set keeps its meaning and stays. The 2-set floor of
 * autoregulation's own decreases is deliberately NOT applied here, because it would
 * change set counts users already see on a base of 2.
 *
 * It reads the raw base. For a myo-rep exercise that is harmless: a combined count of 2
 * or more never trips it, and at 1 `buildSets`' myo-rep branch ignores the adjustment.
 */
export function dropAdjustmentsThatEmptyTheExercise(subSlot: Plan_SubSlot): void {
  const base = subSlot.defaultVolume_sets;
  // if the base itself is missing or damaged there is nothing to measure against
  if (typeof base !== 'number') {
    return;
  }
  // for each path, reset an adjustment that takes the count under 1 set
  for (const path of subSlot.exercisePaths || []) {
    if (path && typeof path.volumeAdjustment === 'number' && base + path.volumeAdjustment < 1) {
      path.volumeAdjustment = 0;
    }
  }
}
